import * as React from 'react';
import { $generateHtmlFromNodes } from '@lexical/html';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { LexicalEditor } from 'lexical';
import { loadHtmlIntoEditor, normalizeEditorHtml } from '../utils';

interface HtmlSyncPluginProps {
  initialHtml?: string;
  onEditorChange?: (value: string, editor: LexicalEditor) => void;
  value?: string;
}

export function HtmlSyncPlugin({
  initialHtml,
  onEditorChange,
  value,
}: HtmlSyncPluginProps) {
  const [editor] = useLexicalComposerContext();
  const lastCommittedHtmlRef = React.useRef(normalizeEditorHtml(initialHtml));

  React.useEffect(() => {
    if (value === undefined) {
      return;
    }

    const nextValue = normalizeEditorHtml(value);

    if (nextValue === lastCommittedHtmlRef.current) {
      return;
    }

    lastCommittedHtmlRef.current = nextValue;
    loadHtmlIntoEditor(editor, nextValue);
  }, [editor, value]);

  return (
    <OnChangePlugin
      ignoreSelectionChange={true}
      onChange={(editorState, currentEditor) => {
        if (!onEditorChange) {
          return;
        }

        // `$generateHtmlFromNodes` needs an active editor (not just an active
        // editor state) to serialize, so read with the editor in context.
        // Without it the call throws and edits never reach the form value.
        editorState.read(
          () => {
            const nextHtml = normalizeEditorHtml(
              $generateHtmlFromNodes(currentEditor, null)
            );

            if (nextHtml === lastCommittedHtmlRef.current) {
              return;
            }

            lastCommittedHtmlRef.current = nextHtml;
            onEditorChange(nextHtml, currentEditor);
          },
          { editor: currentEditor }
        );
      }}
    />
  );
}
